/**
 * One-screen best/worst selection (spec 36-02).
 *
 * The two lists are ORDERED and their order IS the rank, so removing an entry renumbers the
 * survivors automatically - there is no separate rank field to keep in sync, and therefore no way
 * for ranks to develop gaps.
 *
 * Selection order is not a deliberate ranking; report it as selection order (decisions-02).
 */

// Mirrors `MAXDIFF_MODE_MULTI_SET` / `MAXDIFF_MODE_BEST_WORST` in the backend's
// `constants/interview_constants.py` — pinned to stay in step by
// `bestWorstModeConstantsMatchBackend` in `__tests__/bestWorst.test.ts` and by
// `test_frontend_mode_constants_match` in the backend's `tests/survey/test_best_worst.py`.
export const MAXDIFF_MODE_MULTI_SET = 'multi_set';
export const MAXDIFF_MODE_BEST_WORST = 'best_worst_single_set';

export interface BestWorstState {
  best: string[];
  worst: string[];
}

export interface Quotas {
  nBest: number;
  nWorst: number;
}

export function emptyState(): BestWorstState {
  return { best: [], worst: [] };
}

const without = (list: string[], option: string) => list.filter((o) => o !== option);

/** Cycle one option: unselected -> best -> worst -> unselected, skipping any full side. */
export function cycleOption(state: BestWorstState, option: string,
                            quotas: Quotas): BestWorstState {
  if (state.best.includes(option)) {
    const best = without(state.best, option);
    return state.worst.length < quotas.nWorst
      ? { best, worst: [...state.worst, option] }
      : { best, worst: state.worst };
  }
  if (state.worst.includes(option)) {
    return { best: state.best, worst: without(state.worst, option) };
  }
  if (state.best.length < quotas.nBest) {
    return { ...state, best: [...state.best, option] };
  }
  if (state.worst.length < quotas.nWorst) {
    return { ...state, worst: [...state.worst, option] };
  }
  return state; // both sides full: the tap is a no-op
}

export function isComplete(state: BestWorstState, quotas: Quotas): boolean {
  return state.best.length === quotas.nBest && state.worst.length === quotas.nWorst;
}

export function toResponseValue(state: BestWorstState) {
  const rank = (list: string[]) => list.map((option, i) => ({ option, rank: i + 1 }));
  return { best: rank(state.best), worst: rank(state.worst) };
}
